/**
 * ProjectConversationsTab — every thread in the project, newest first.
 *
 * Three filters, because the three questions people actually ask of a shared
 * workspace are "what did *I* touch", "what did we do with *that agent*", and
 * "what has *she* been working on". Contributor and creator are deliberately
 * separate filters: who started a thread and who has written in it are
 * different facts, and in a shared project they diverge constantly.
 *
 * Each row carries the contributors' faces, which is how "who contributed
 * what" is answered at a glance rather than by opening every thread.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { activeLocale } from '@/utils/locale';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { useTranslator } from '../hooks/useTranslator';
import { useTenantPath } from '@/routing';
import { ConfirmModal } from '@/components/common/ConfirmModal';
import conversationService from '@modules/conversations/services/conversationService';
import projectService from '../services/projectService';
import type {
  ProjectAgent,
  ProjectConversation,
  ProjectMemberRow,
  ProjectSummary,
} from '../types';
import { PersonAvatarStack } from './PersonAvatar';
import { SkeletonRows, EmptyBlock, ErrorBlock } from './StateBlocks';
import './ProjectTabs.css';

interface ProjectConversationsTabProps {
  project: ProjectSummary;
  agents: ProjectAgent[];
  members: ProjectMemberRow[];
  /** Opens the agent chooser — the tab's primary action and its empty state. */
  onNewConversation: () => void;
  /** Bumped by the page after a new thread is created, to force a refetch. */
  refreshToken?: number;
}

function formatWhen(iso: string | null | undefined, t: (k: string) => string): string {
  if (!iso) return '';
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return '';
  const minutes = Math.floor((Date.now() - then) / 60000);
  if (minutes < 1) return t('time.justNow') || 'just now';
  if (minutes < 60) return (t('time.minutesAgo') || '{{n}}m ago').replace('{{n}}', String(minutes));
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return (t('time.hoursAgo') || '{{n}}h ago').replace('{{n}}', String(hours));
  const days = Math.floor(hours / 24);
  if (days < 30) return (t('time.daysAgo') || '{{n}}d ago').replace('{{n}}', String(days));
  return new Date(iso).toLocaleDateString(activeLocale());
}

export function ProjectConversationsTab({
  project,
  agents,
  members,
  onNewConversation,
  refreshToken = 0,
}: ProjectConversationsTabProps) {
  const { t, tRef } = useTranslator();
  const { user } = useAuth();
  const navigate = useNavigate();
  const tenantPath = useTenantPath();

  const [conversations, setConversations] = useState<ProjectConversation[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [agentFilter, setAgentFilter] = useState<string | null>(null);
  const [contributorFilter, setContributorFilter] = useState<string | null>(null);
  const [creatorFilter, setCreatorFilter] = useState<string | null>(null);
  const [pendingDelete, setPendingDelete] = useState<ProjectConversation | null>(null);
  const [deleting, setDeleting] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    setError(null);
    projectService
      .listConversations(project.project_id, {
        chatbot_id: agentFilter || undefined,
        contributor_id: contributorFilter || undefined,
        created_by: creatorFilter || undefined,
      })
      .then(setConversations)
      .catch(() => setError(
        tRef.current('projects.conversations.loadFailed')
          || t('projects.conversations.loadFailed'),
      ))
      .finally(() => setLoading(false));
    // `t` out of the deps on purpose — see useTranslator.ts.
  }, [project.project_id, agentFilter, contributorFilter, creatorFilter, tRef, refreshToken]);

  useEffect(() => { load(); }, [load]);

  /** Everyone who can be filtered on: accepted individual members plus the owner. */
  const people = useMemo(() => {
    const rows = members
      .filter(m => m.status === 'accepted' && m.member_user_id)
      .map(m => ({
        id: m.member_user_id as string,
        name: m.member_name || m.member_email || '',
        tenantName: m.member_tenant_name || null,
      }));
    rows.unshift({
      id: project.created_by_user_id,
      name: project.created_by_name || project.created_by_email || '',
      tenantName: project.owner_tenant_name || null,
    });
    // Dedup: the creator can also appear as a member row after a re-invite.
    const seen = new Set<string>();
    return rows.filter(r => (seen.has(r.id) ? false : (seen.add(r.id), true)));
  }, [members, project]);

  const filtersActive = !!(agentFilter || contributorFilter || creatorFilter);

  const clearFilters = () => {
    setAgentFilter(null);
    setContributorFilter(null);
    setCreatorFilter(null);
  };

  const handleDelete = async () => {
    if (!pendingDelete) return;
    setDeleting(true);
    try {
      await conversationService.deleteConversation(pendingDelete.conversation_id);
      setConversations(prev =>
        prev.filter(c => c.conversation_id !== pendingDelete.conversation_id));
      setPendingDelete(null);
    } catch {
      setError(t('projects.conversations.deleteFailed') || 'Could not delete that conversation.');
    } finally {
      setDeleting(false);
    }
  };

  /** Managers may delete anything here; everyone else only their own threads. */
  const mayDelete = (conversation: ProjectConversation) =>
    project.can_manage || conversation.user_id === user?.user_id;

  const body = (() => {
    if (loading) return <SkeletonRows rows={3} />;
    if (error) return <ErrorBlock message={error} onRetry={load} />;

    if (conversations.length === 0) {
      return filtersActive ? (
        <EmptyBlock
          icon={(
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
              <path d="M4 5h16M7 12h10M10 19h4" />
            </svg>
          )}
          title={t('projects.conversations.noMatchesTitle') || 'Nothing matches these filters'}
          body={t('projects.conversations.noMatchesBody')
            || 'Try widening the filters to see more of the project.'}
          action={(
            <button type="button" className="pj-btn pj-btn-secondary" onClick={clearFilters}>
              {t('projects.conversations.clearFilters') || 'Clear filters'}
            </button>
          )}
        />
      ) : (
        <EmptyBlock
          icon={(
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
              <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
            </svg>
          )}
          title={t('projects.conversations.emptyTitle') || 'No conversations yet'}
          body={t('projects.conversations.emptyBody')
            || 'Start one with any agent you have access to. It gets the project brief and files, and everyone here can pick it up.'}
          action={project.is_archived ? undefined : (
            <button type="button" className="pj-btn pj-btn-primary" onClick={onNewConversation}>
              {t('projects.conversations.start') || 'Start a conversation'}
            </button>
          )}
        />
      );
    }

    return (
      <ul className="conv-list">
        {conversations.map(conversation => (
          <li key={conversation.conversation_id}>
            <div className="conv-row">
              <button
                type="button"
                className="conv-row-main"
                onClick={() => navigate(
                  tenantPath(`/chat?conversation=${conversation.conversation_id}`),
                )}
              >
                <span dir="auto" className="conv-row-title">
                  {conversation.conversation_title
                    || (t('projects.conversations.untitled') || 'Untitled conversation')}
                  {/* Marked in the LIST, not only inside the thread: otherwise you
                      discover a thread is read-only by opening it and finding the
                      composer replaced, which reads as something broken. */}
                  {conversation.thread_mode === 'solo' && (
                    <span className="conv-row-solo">
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"
                           strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                        <circle cx="12" cy="8" r="3.4" />
                        <path d="M5.5 20a6.5 6.5 0 0 1 13 0" />
                      </svg>
                      {conversation.user_id && conversation.user_id === user?.user_id
                        ? (t('projects.thread.soloBadgeMine') || 'Only I can write')
                        : (t('projects.thread.soloBadge') || 'Only they can write')}
                    </span>
                  )}
                </span>
                <span className="conv-row-meta">
                  {conversation.chatbot_name && (
                    <span className="conv-row-agent" dir="auto">{conversation.chatbot_name}</span>
                  )}
                  <span>
                    {(t('projects.conversations.messageCount') || '{{n}} messages')
                      .replace('{{n}}', String(conversation.message_count ?? 0))}
                  </span>
                  <span>{formatWhen(conversation.last_message_at || conversation.created_at, t)}</span>
                </span>
              </button>

              <div className="conv-row-side">
                <PersonAvatarStack
                  people={conversation.contributors.map(c => ({
                    id: c.user_id,
                    name: c.name,
                    email: c.email,
                  }))}
                  max={3}
                  size={24}
                />
                {mayDelete(conversation) && (
                  <button
                    type="button"
                    className="pj-icon-btn conv-row-delete"
                    onClick={() => setPendingDelete(conversation)}
                    aria-label={t('projects.conversations.delete') || 'Delete conversation'}
                    title={t('projects.conversations.delete') || 'Delete conversation'}
                  >
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M3 6h18M8 6V4h8v2M19 6l-1 14H6L5 6" />
                    </svg>
                  </button>
                )}
              </div>
            </div>
          </li>
        ))}
      </ul>
    );
  })();

  return (
    <div className="pj-tab">
      <div className="conv-toolbar">
        <div className="conv-filters">
          <FilterSelect
            label={t('projects.conversations.filterAgent') || 'Agent'}
            value={agentFilter}
            onChange={setAgentFilter}
            options={agents.map(a => ({ value: a.chatbot_id, label: a.chatbot_name }))}
            allLabel={t('projects.conversations.allAgents') || 'All agents'}
          />
          <FilterSelect
            label={t('projects.conversations.filterContributor') || 'Contributor'}
            value={contributorFilter}
            onChange={setContributorFilter}
            options={people.map(p => ({ value: p.id, label: p.name }))}
            allLabel={t('projects.conversations.allContributors') || 'Anyone'}
          />
          <FilterSelect
            label={t('projects.conversations.filterCreator') || 'Started by'}
            value={creatorFilter}
            onChange={setCreatorFilter}
            options={people.map(p => ({ value: p.id, label: p.name }))}
            allLabel={t('projects.conversations.allCreators') || 'Anyone'}
          />
          {user && (
            <button
              type="button"
              className={`pj-chip ${contributorFilter === user.user_id ? 'is-active' : ''}`}
              onClick={() => setContributorFilter(
                contributorFilter === user.user_id ? null : user.user_id,
              )}
              aria-pressed={contributorFilter === user.user_id}
            >
              {t('projects.conversations.onlyMine') || 'Only mine'}
            </button>
          )}
          {filtersActive && (
            <button type="button" className="pj-btn pj-btn-ghost pj-btn-sm" onClick={clearFilters}>
              {t('projects.conversations.clearFilters') || 'Clear filters'}
            </button>
          )}
        </div>

        {!project.is_archived && (
          <button type="button" className="pj-btn pj-btn-primary" onClick={onNewConversation}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
              <line x1="12" y1="5" x2="12" y2="19" />
              <line x1="5" y1="12" x2="19" y2="12" />
            </svg>
            {t('projects.conversations.new') || 'New conversation'}
          </button>
        )}
      </div>

      {body}

      <ConfirmModal
        isOpen={!!pendingDelete}
        onClose={() => setPendingDelete(null)}
        onConfirm={handleDelete}
        title={t('projects.conversations.deleteTitle') || 'Delete this conversation?'}
        message={(t('projects.conversations.deleteBody')
          || 'This removes “{{title}}” and all of its messages for everyone in the project. This cannot be undone.')
          .replace('{{title}}', pendingDelete?.conversation_title
            || (t('projects.conversations.untitled') || 'Untitled conversation'))}
        confirmText={t('common.delete') || 'Delete'}
        cancelText={t('common.cancel') || 'Cancel'}
        variant="danger"
        loading={deleting}
      />
    </div>
  );
}

/** A labelled native select — accessible, keyboard-complete, and RTL-correct. */
function FilterSelect({
  label,
  value,
  onChange,
  options,
  allLabel,
}: {
  label: string;
  value: string | null;
  onChange: (next: string | null) => void;
  options: Array<{ value: string; label: string }>;
  allLabel: string;
}) {
  if (options.length === 0) return null;
  return (
    <label className={`conv-filter ${value ? 'is-active' : ''}`}>
      <span className="conv-filter-label">{label}</span>
      <select
        className="conv-filter-select"
        value={value || ''}
        onChange={e => onChange(e.target.value || null)}
      >
        <option value="">{allLabel}</option>
        {options.map(option => (
          <option key={option.value} value={option.value}>{option.label}</option>
        ))}
      </select>
    </label>
  );
}

export default ProjectConversationsTab;
